/**
 * Data layer for `saved-report-templates`. Thin typed fetch wrappers over /api/v1/saved-report-templates.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface ReportTemplateDefinition {
	report_type?: string;
	date_range?: string;
	filters?: Record<string, unknown>;
	group_by?: string[];
	columns?: string[];
	metrics?: string[];
	schedule?: string | null;
	[key: string]: unknown;
}

export interface SavedReportTemplateRow {
	id: string;
	tenant_id: string;
	name: string;
	definition: ReportTemplateDefinition;
	created_by?: string | null;
	last_run_at?: number | null;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListSavedReportTemplatesResponse {
	count: number;
	items: SavedReportTemplateRow[];
}

export interface CreateSavedReportTemplateBody {
	name: string;
	report_type?: string;
	date_range?: string;
	filters?: Record<string, unknown>;
	group_by?: string[];
	columns?: string[];
	schedule?: string | null;
	[key: string]: unknown;
}

const BASE = "/api/v1/saved-report-templates";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListSavedReportTemplatesResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListSavedReportTemplatesResponse>(res);
}

export async function create(
	body: CreateSavedReportTemplateBody,
): Promise<SavedReportTemplateRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<SavedReportTemplateRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const savedReportTemplatesQueryKeys = {
	all: ["saved-report-templates"] as const,
	list: () => [...savedReportTemplatesQueryKeys.all] as const,
	detail: (id: string) => [...savedReportTemplatesQueryKeys.all, id] as const,
};
